import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { getToken, setToken, getStoredUser, setStoredUser } from "@/lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => getStoredUser());
  const [loading, setLoading] = useState(!!getToken() && !getStoredUser());
  const [theme,   setTheme]   = useState(() => localStorage.getItem("events.theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("events.theme", theme);
  }, [theme]);

  // Re-hydrate user from server on page reload (token exists but no cached user)
  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token || user) return () => { cancelled = true; };
    setLoading(true);
    api.whoami()
      .then(res => {
        if (cancelled) return;
        const u = res?.auth || res;
        if (u?.email) finalise(token, u);
        else { setToken(null); setStoredUser(null); setUser(null); }
      })
      .catch(() => {
        if (!cancelled) { setToken(null); setStoredUser(null); setUser(null); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  function finalise(token, u) {
    const userObj = {
      email:        u.email,
      role:         u.role,
      myHallIds:    u.myHallIds || [],
      isSuperAdmin: u.role === "superadmin",
      name:         u.email?.split("@")[0] || u.email,
    };
    setToken(token);           // writes to "events.token" (TOKEN_KEY)
    setStoredUser(userObj);    // writes to "events.user"
    setUser(userObj);

    // Also write keys that CheckIn + Gallery pages read directly from localStorage
    localStorage.setItem("authToken",  token);          // checkin.html reads this
    localStorage.setItem("authEmail",  u.email);
    localStorage.setItem("authRole",   u.role);
    localStorage.setItem("authHalls",  JSON.stringify(u.myHallIds || []));

    return userObj;
  }

  const value = useMemo(() => ({
    user,
    loading,
    isAuthed:     !!user && !!getToken(),
    isSuperAdmin: user?.role === "superadmin",
    isIncharge:   user?.role === "incharge" || user?.role === "superadmin",
    theme,
    toggleTheme:  () => setTheme(t => t === "dark" ? "light" : "dark"),

    async requestOtp(email) {
      const res = await api.requestOtp(email);
      if (!res.success) throw new Error(res.message || "Could not send OTP");
      return res;
    },

    async verifyOtp(email, code) {
      const res = await api.verifyOtp(email, code);
      if (!res.success || !res.token) throw new Error(res.message || "Login failed");
      return finalise(res.token, {
        email:      res.email,
        role:       res.role,
        myHallIds:  res.myHallIds || [],
      });
    },

    logout() {
      setToken(null);
      setStoredUser(null);
      setUser(null);
      // Clear all auth-related localStorage keys, including the legacy
      // pre-multi-tenant keys (bmsit.token / bmsit.user).
      //
      // WHY bmsit.token / bmsit.user must be cleared:
      //   The migration IIFE in api.js runs every time the module is imported
      //   (i.e. on every page load). It checks: if events.token is missing AND
      //   bmsit.token exists, copy bmsit.token → events.token. If we only clear
      //   events.token on logout but leave bmsit.token behind, a page refresh
      //   triggers the migration IIFE which silently restores the token, then
      //   the re-hydration whoami call restores the full session — so logout
      //   appears to work but is completely undone on any page refresh.
      //
      // bicep_welcome_seen is included so a second user on the same device
      // sees the BICEP welcome strip.
      //
      // events.institute and events.theme are intentionally NOT cleared —
      // institute persists for the next login picker; theme is a UX preference.
      [
        "events.token", "events.user",
        "authToken", "authEmail", "authRole", "authHalls",
        "bicep_welcome_seen",
        // Legacy pre-multi-tenant keys — must be cleared so the api.js
        // migration IIFE cannot restore the token from them after logout.
        "bmsit.token", "bmsit.user",
      ].forEach(k => localStorage.removeItem(k));
    },
  }), [user, loading, theme]); // eslint-disable-line

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be inside AuthProvider");
  return v;
};

export const homeFor = (u) => {
  if (!u) return "/login";
  if (u.role === "superadmin" || u.role === "incharge") return "/admin";
  if (u.role === "photographer") return "/gallery-upload";
  return "/portal";
};
